import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format, addMonths, parseISO } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { recordsAPI, inspectionsAPI } from '../lib/api';
import { toast } from 'sonner';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Search,
  Loader2,
  ArrowLeft,
  Calendar,
  CheckCircle,
  XCircle,
  MinusCircle,
  AlertTriangle,
  Lock,
  RefreshCw,
  FileText,
  Car,
  Truck,
  Package,
  ClipboardCheck,
  Shield,
  Upload,
  Trash2,
  FlaskConical,
  Gauge,
  Eye,
  Award,
  Cylinder,
} from 'lucide-react';
import { cn } from '../lib/utils';

// Reusable test result buttons (Pass / Fail / Not Conducted)
const TestResultButtons = ({ value, onChange, testId = '' }) => {
  const options = [
    { key: 'pass', label: 'Pass', icon: CheckCircle, activeClass: 'bg-emerald-600 hover:bg-emerald-700' },
    { key: 'fail', label: 'Fail', icon: XCircle, activeClass: 'bg-red-600 hover:bg-red-700' },
    { key: 'not_conducted', label: 'Not Conducted', icon: MinusCircle, activeClass: 'bg-gray-500 hover:bg-gray-600 text-white' },
  ];
  return (
    <div className="flex gap-2 flex-wrap">
      {options.map(({ key, label, icon: Icon, activeClass }) => (
        <Button
          key={key}
          variant={value === key ? 'default' : 'outline'}
          size="sm"
          type="button"
          onClick={() => onChange(key)}
          className={value === key ? activeClass : ''}
          data-testid={testId ? `${testId}-${key}` : undefined}
        >
          <Icon className="mr-1 h-4 w-4" /> {label}
        </Button>
      ))}
    </div>
  );
};

const VALIDITY_OPTIONS = [
  { value: '6', label: '6 Months' },
  { value: '12', label: '12 Months' },
];

const OVERALL_RESULTS = [
  { value: 'approved', label: 'Approved', color: 'bg-emerald-500', description: 'All tests passed, certificate will be generated' },
  { value: 'conditionally_approved', label: 'Conditionally Approved', color: 'bg-amber-500', description: 'Approved with conditions noted on certificate' },
  { value: 'rejected', label: 'Rejected', color: 'bg-red-500', description: 'Inspection failed, no certificate will be generated' },
];

const getRecordTypeLabel = (type) => {
  const labels = {
    'car': 'Light Vehicle',
    '3w': '3-Wheeler',
    'bus': 'Bus',
    'truck': 'Truck',
    'trailer': 'Trailer (CNG Tube)',
    'cng_storage': 'CNG Storage',
  };
  return labels[type] || type;
};

const getRecordTypeIcon = (type) => {
  if (type === 'trailer') return Truck;
  if (type === 'cng_storage') return Package;
  return Car;
};

export default function RecertificationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  
  const inspectionType = searchParams.get('type') || 'periodic';
  
  const [step, setStep] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [existingInspection, setExistingInspection] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  
  // Editable fields
  const [inspectionDate, setInspectionDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [validityMonths, setValidityMonths] = useState('12');
  
  // Test Results - All 4 tests exactly like New Inspection
  const [testResults, setTestResults] = useState({
    visual_inspection: null,  // Always mandatory
    gas_leak_test: null,      // Always mandatory
    ndt_test: null,           // Mandatory for trailer/storage
    pressure_test: null,      // Optional (mandatory upload if conducted for trailer/storage)
    overall_result: null,     // Mandatory - Approved/Conditionally Approved/Rejected
  });
  
  // Test Report Files - Fresh uploads required
  const [testReportFiles, setTestReportFiles] = useState({
    leak_test_report: null,      // Required for trailer/storage
    ndt_test_report: null,       // Required for trailer/storage
    pressure_test_report: null,  // Required if pressure test conducted for trailer/storage
  });
  
  const [uploading, setUploading] = useState({
    leak_test_report: false,
    ndt_test_report: false,
    pressure_test_report: false,
  });
  
  // Track expired cylinders
  const [expiredCylinders, setExpiredCylinders] = useState([]);
  
  // Calculate validity end date - ensure validityMonths is always a number
  // Use parseISO to avoid timezone issues when parsing date strings
  // new Date('YYYY-MM-DD') interprets the date as UTC midnight, which can shift
  // to the previous day in timezones ahead of UTC. parseISO treats it as local date.
  const validTo = inspectionDate ? 
    format(addMonths(parseISO(inspectionDate), Number(validityMonths)), 'yyyy-MM-dd') : '';

  // Determine if this is a special type (trailer or storage)
  const isTrailerOrStorage = selectedRecord?.record_type === 'trailer' || selectedRecord?.record_type === 'cng_storage';
  const isTrailer = selectedRecord?.record_type === 'trailer';
  const typeLabel = isTrailer ? 'Trailer' : 'Storage';

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter a registration number or serial number');
      return;
    }
    
    setSearching(true);
    setSearchResults([]);
    try {
      const response = await recordsAPI.searchExact(searchQuery.trim());
      setSearchResults(response.data);
      if (response.data.length === 0) {
        toast.info('No exact match found. Please enter the complete registration/serial number.');
      } else if (response.data.length === 1) {
        toast.success('Record found!');
      }
    } catch (err) {
      console.error('Search failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to search records');
    } finally {
      setSearching(false);
    }
  };

  const handleSelectRecord = async (record) => {
    setSelectedRecord(record);
    setLoading(true);
    
    try {
      if (record.latest_inspection) {
        setExistingInspection(record.latest_inspection);
      } else {
        toast.error('No existing inspection found for this record. Please create a new inspection.');
        setSelectedRecord(null);
        return;
      }
      setStep(2);
    } catch (err) {
      console.error('Failed to load inspection:', err);
      toast.error('Failed to load existing inspection');
      setSelectedRecord(null);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (fieldName, file) => {
    if (!file) return;
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }
    
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a PDF or image file (JPEG, PNG)');
      return;
    }
    
    setUploading(prev => ({ ...prev, [fieldName]: true }));
    
    // For now, store the file locally - in production this would upload to server
    setTestReportFiles(prev => ({ ...prev, [fieldName]: file }));
    toast.success(`${file.name} attached successfully`);
    
    setUploading(prev => ({ ...prev, [fieldName]: false }));
  };

  const handleRemoveFile = (fieldName) => {
    setTestReportFiles(prev => ({ ...prev, [fieldName]: null }));
  };

  const validateForm = () => {
    // Validate selected record has required fields (auto-fetched but must be present)
    if (!selectedRecord) {
      toast.error('No record selected');
      return false;
    }
    
    // Validate registration/serial number is present
    const identifier = selectedRecord.registration_number || selectedRecord.storage_serial_number || selectedRecord.serial_number;
    if (!identifier) {
      toast.error('Vehicle Registration Number or Storage Serial Number is missing from the record');
      return false;
    }
    
    // Validate owner phone is present
    if (!selectedRecord.owner_phone && !selectedRecord.owner_name) {
      toast.error('Owner information is missing from the record');
      return false;
    }
    
    // Validate inspection date is selected
    if (!inspectionDate) {
      toast.error('Inspection date is required');
      return false;
    }
    
    // Validate inspection date is not in the future (beyond reasonable limit)
    const selectedDate = new Date(inspectionDate);
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    if (selectedDate > today) {
      toast.error('Inspection date cannot be in the future');
      return false;
    }
    
    // **CRITICAL: Check for expired cylinders**
    const cylinders = selectedRecord.cylinders || [];
    if (cylinders.length > 0) {
      const inspDate = parseISO(inspectionDate);
      const expiredCyls = [];
      
      for (let i = 0; i < cylinders.length; i++) {
        const cyl = cylinders[i];
        if (cyl.expiry_date) {
          const expiryDate = parseISO(cyl.expiry_date);
          if (expiryDate < inspDate) {
            expiredCyls.push({
              index: i,
              serial: cyl.serial_number || `Cylinder ${i + 1}`,
              expiry_date: cyl.expiry_date
            });
          }
        }
      }
      
      if (expiredCyls.length > 0) {
        setExpiredCylinders(expiredCyls.map(c => c.index));
        const expiredList = expiredCyls.map(c => 
          `${c.serial} (expired ${format(parseISO(c.expiry_date), 'dd MMM yyyy')})`
        ).join(', ');
        toast.error(
          `Cannot proceed with renewal: ${expiredCyls.length} cylinder(s) have expired before the inspection date: ${expiredList}. Expired cylinders must be replaced before renewal inspection. Certificate and QR code will not be generated.`,
          { duration: 12000 }
        );
        return false;
      } else {
        setExpiredCylinders([]);
      }
    }
    
    // Visual Inspection is always mandatory
    if (!testResults.visual_inspection) {
      toast.error('Please select a result for Visual Inspection (Pass, Fail, or Not Conducted)');
      return false;
    }
    
    // Gas Leak Test is always mandatory
    if (!testResults.gas_leak_test) {
      toast.error('Please select a result for Gas Leak Test (Pass, Fail, or Not Conducted)');
      return false;
    }

    // For trailer/storage
    if (isTrailerOrStorage) {
      // NDT test selection is mandatory
      if (!testResults.ndt_test) {
        toast.error(`Please select a result for NDT test (Pass, Fail, or Not Conducted) — required for ${typeLabel} inspections`);
        return false;
      }
      
      // Leak test report is mandatory only if the test was conducted
      if (testResults.gas_leak_test !== 'not_conducted' && !testReportFiles.leak_test_report) {
        toast.error(`Gas Leak test report upload is required for ${typeLabel} when the test is conducted`);
        return false;
      }
      
      // NDT test report is mandatory only if the test was conducted
      if (testResults.ndt_test !== 'not_conducted' && !testReportFiles.ndt_test_report) {
        toast.error(`NDT test report/certificate upload is required for ${typeLabel} when the test is conducted`);
        return false;
      }
      
      // Pressure test report is mandatory only if pressure test was conducted (pass/fail)
      if (testResults.pressure_test && testResults.pressure_test !== 'not_conducted' && !testReportFiles.pressure_test_report) {
        toast.error('Pressure test report/certificate upload is required when pressure test is conducted');
        return false;
      }
    }

    // Overall Result is always mandatory
    if (!testResults.overall_result) {
      toast.error('Overall Result is required - please select Approved, Conditionally Approved, or Rejected');
      return false;
    }

    return true;
  };

  const handleSubmitClick = () => {
    if (!validateForm()) return;
    setConfirmDialogOpen(true);
  };

  const handleConfirmSubmit = async () => {
    setConfirmDialogOpen(false);
    
    if (!selectedRecord || !existingInspection) {
      toast.error('No record selected');
      return;
    }
    
    setSubmitting(true);
    try {
      // Prepare test results payload
      // Note: valid_to is NOT sent - it's auto-calculated on the backend
      // using proper calendar month addition
      const payload = {
        inspection_type: inspectionType,
        inspection_date: inspectionDate,
        validity_months: parseInt(validityMonths),
        valid_from: inspectionDate,
        // valid_to is auto-calculated by backend - do not send manually
        // Test results
        visual_inspection_passed: testResults.visual_inspection === 'pass' ? true : testResults.visual_inspection === 'fail' ? false : null,
        gas_leak_test_passed: testResults.gas_leak_test === 'pass' ? true : testResults.gas_leak_test === 'fail' ? false : null,
        pressure_test_passed: testResults.pressure_test === 'pass' ? true : testResults.pressure_test === 'fail' ? false : null,
        ndt_test_passed: testResults.ndt_test === 'pass' ? true : testResults.ndt_test === 'fail' ? false : null,
        // Overall result - mandatory
        overall_result: testResults.overall_result,
      };

      // Call the recertification endpoint
      await inspectionsAPI.recertify(existingInspection.id, payload);
      
      toast.success('Inspection recertified successfully!');
      
      // Navigate to certificate actions page
      navigate(`/inspections/${existingInspection.id}/certificate-actions?renewal=true`);
    } catch (err) {
      console.error('Recertification failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to recertify inspection');
    } finally {
      setSubmitting(false);
    }
  };

  const inspectionTypeLabel = inspectionType === 'periodic' ? 'Periodic Inspection' : 'After Accident / Repair';

  return (
    <div className="max-w-4xl mx-auto space-y-6 p-4" data-testid="recertification-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">{inspectionTypeLabel}</h1>
          <p className="text-muted-foreground">
            Search for existing record and perform renewal inspection
          </p>
        </div>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center gap-4">
        <div className={cn(
          "flex items-center gap-2 px-4 py-2 rounded-full",
          step === 1 ? "bg-primary text-primary-foreground" : "bg-muted"
        )}>
          <Search className="h-4 w-4" />
          <span className="text-sm font-medium">1. Search Record</span>
        </div>
        <div className="h-0.5 w-8 bg-muted" />
        <div className={cn(
          "flex items-center gap-2 px-4 py-2 rounded-full",
          step === 2 ? "bg-primary text-primary-foreground" : "bg-muted"
        )}>
          <RefreshCw className="h-4 w-4" />
          <span className="text-sm font-medium">2. Update & Recertify</span>
        </div>
      </div>

      {/* Step 1: Search */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="h-5 w-5 text-primary" />
              Search Existing Record
            </CardTitle>
            <CardDescription>
              Enter the vehicle registration number or CNG storage serial number
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex gap-4">
              <div className="flex-1">
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value.toUpperCase())}
                  placeholder="Enter registration number or serial number..."
                  className="font-mono text-lg"
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  data-testid="search-input"
                />
              </div>
              <Button onClick={handleSearch} disabled={searching}>
                {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                <span className="ml-2">Search</span>
              </Button>
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="space-y-3">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  Search Results ({searchResults.length})
                </h3>
                <div className="space-y-2">
                  {searchResults.map((record) => {
                    const Icon = getRecordTypeIcon(record.record_type);
                    const hasValidInspection = record.latest_inspection?.status === 'valid';
                    
                    return (
                      <div
                        key={record.id}
                        className={cn(
                          "p-4 border rounded-lg cursor-pointer hover:border-primary transition-colors",
                          selectedRecord?.id === record.id && "border-primary bg-primary/5"
                        )}
                        onClick={() => handleSelectRecord(record)}
                        data-testid={`search-result-${record.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={cn(
                              "h-10 w-10 rounded-lg flex items-center justify-center",
                              hasValidInspection ? "bg-emerald-100" : "bg-amber-100"
                            )}>
                              <Icon className={cn(
                                "h-5 w-5",
                                hasValidInspection ? "text-emerald-600" : "text-amber-600"
                              )} />
                            </div>
                            <div>
                              <p className="font-mono font-bold">
                                {record.registration_number || record.storage_serial_number || record.serial_number}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {getRecordTypeLabel(record.record_type)}
                                {record.owner_name && ` • ${record.owner_name}`}
                              </p>
                            </div>
                          </div>
                          {record.latest_inspection && (
                            <Badge variant={hasValidInspection ? 'default' : 'secondary'}>
                              {record.latest_inspection.status}
                            </Badge>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {searchResults.length === 0 && searchQuery && !searching && (
              <div className="text-center py-8 text-muted-foreground">
                <AlertTriangle className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>No records found for "{searchQuery}"</p>
                <p className="text-sm mt-1">Please check the registration/serial number and try again.</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 2: Review & Update */}
      {step === 2 && selectedRecord && existingInspection && (
        <>
          {/* Existing Record Info - Read Only */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Existing Record (Read-Only)
              </CardTitle>
              <CardDescription>
                Vehicle/facility details cannot be modified during renewal
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                    <Lock className="h-3 w-3" /> Registration/Serial
                  </p>
                  <p className="font-mono font-bold">
                    {selectedRecord.registration_number || selectedRecord.storage_serial_number || selectedRecord.serial_number}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                    <Lock className="h-3 w-3" /> Type
                  </p>
                  <p className="font-medium">{getRecordTypeLabel(selectedRecord.record_type)}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                    <Lock className="h-3 w-3" /> Owner Name
                  </p>
                  <p className="font-medium">{selectedRecord.owner_name || 'N/A'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                    <Lock className="h-3 w-3" /> Make/Manufacturer
                  </p>
                  <p className="font-medium">{selectedRecord.make || selectedRecord.manufacturer || 'N/A'}</p>
                </div>
              </div>

              {/* Previous Inspection Summary */}
              <div className="p-4 border rounded-lg">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <ClipboardCheck className="h-4 w-4" />
                  Previous Inspection
                </h4>
                <div className="grid md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Certificate #</p>
                    <p className="font-mono">{existingInspection.certificate_number || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Valid From</p>
                    <p>{existingInspection.valid_from ? format(parseISO(existingInspection.valid_from), 'dd MMM yyyy') : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Valid To</p>
                    <p>{existingInspection.valid_to ? format(parseISO(existingInspection.valid_to), 'dd MMM yyyy') : 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Cylinders Summary with Expiry Warning */}
              {selectedRecord.cylinders && selectedRecord.cylinders.length > 0 && (
                <div className="p-4 border rounded-lg space-y-3">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Cylinder className="h-4 w-4" />
                    Registered Cylinders ({selectedRecord.cylinders.length})
                  </h4>
                  
                  {/* Warning banner if any cylinders will be expired on inspection date */}
                  {inspectionDate && (() => {
                    const inspDate = parseISO(inspectionDate);
                    const expiredCyls = selectedRecord.cylinders.filter(cyl => {
                      if (cyl.expiry_date) {
                        return parseISO(cyl.expiry_date) < inspDate;
                      }
                      return false;
                    });
                    
                    if (expiredCyls.length > 0) {
                      return (
                        <div className="p-4 bg-red-50 dark:bg-red-950/30 border border-red-300 dark:border-red-700 rounded-lg">
                          <div className="flex items-start gap-3">
                            <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5 animate-pulse" />
                            <div>
                              <h5 className="font-semibold text-red-800 dark:text-red-300">
                                Expired Cylinder(s) Detected - Renewal Blocked
                              </h5>
                              <p className="text-sm text-red-700 dark:text-red-400 mt-1">
                                {expiredCyls.length} cylinder(s) have expired before the selected inspection date.
                                Expired cylinders must be replaced before renewal inspection can proceed.
                                Certificate and QR code will not be generated.
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  
                  <div className="grid gap-2">
                    {selectedRecord.cylinders.map((cyl, idx) => {
                      const inspDate = inspectionDate ? parseISO(inspectionDate) : new Date();
                      const isExpired = cyl.expiry_date && parseISO(cyl.expiry_date) < inspDate;
                      
                      return (
                        <div 
                          key={idx} 
                          className={cn(
                            "flex items-center justify-between p-3 rounded-md text-sm",
                            isExpired 
                              ? "bg-red-100 dark:bg-red-950/40 border border-red-300 dark:border-red-700" 
                              : "bg-muted/50"
                          )}
                        >
                          <div className="flex items-center gap-3">
                            <span className="font-mono font-medium">{cyl.serial_number}</span>
                            <Badge variant="outline" className="text-xs">Type {cyl.cylinder_type}</Badge>
                            {isExpired && (
                              <Badge variant="destructive" className="animate-pulse">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                EXPIRED
                              </Badge>
                            )}
                          </div>
                          <div className={cn(
                            "text-xs",
                            isExpired ? "text-red-600 dark:text-red-400 font-medium" : "text-muted-foreground"
                          )}>
                            Expires: {cyl.expiry_date ? format(parseISO(cyl.expiry_date), 'dd MMM yyyy') : 'N/A'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Editable Fields - Date & Validity */}
          <Card className="border-primary">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                New Inspection Date & Validity
              </CardTitle>
              <CardDescription>
                Update the inspection date and validity period
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-base font-semibold">New Inspection Date *</Label>
                  <Input
                    type="date"
                    value={inspectionDate}
                    onChange={(e) => setInspectionDate(e.target.value)}
                    data-testid="inspection-date"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-base font-semibold">Validity Period *</Label>
                  <RadioGroup
                    value={validityMonths}
                    onValueChange={setValidityMonths}
                    className="flex gap-4"
                  >
                    {VALIDITY_OPTIONS.map((opt) => (
                      <div key={opt.value} className="flex items-center space-x-2">
                        <RadioGroupItem value={opt.value} id={`validity-${opt.value}`} />
                        <Label htmlFor={`validity-${opt.value}`} className="cursor-pointer">
                          {opt.label}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              </div>

              {/* Calculated Valid To */}
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-emerald-700">New Valid Until</p>
                    <p className="text-xl font-semibold text-emerald-800">
                      {validTo ? format(parseISO(validTo), 'dd MMMM yyyy') : '-'}
                    </p>
                  </div>
                  <Calendar className="h-8 w-8 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Test Results - All 4 Tests with File Uploads */}
          <Card className="border-blue-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FlaskConical className="h-5 w-5 text-blue-600" />
                Test Results & Reports
              </CardTitle>
              <CardDescription>
                Record inspection test results {isTrailerOrStorage && `(${typeLabel} inspections require NDT test and file uploads)`}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Mandatory Tests Section */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Mandatory Tests</h3>
                
                {/* 1. Visual Inspection - Always Mandatory, No File Upload */}
                <div className="p-4 border rounded-lg">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <p className="font-medium">Detailed Visual Inspection *</p>
                      <p className="text-sm text-muted-foreground">Check for physical damage, corrosion, dents</p>
                    </div>
                    <TestResultButtons
                      value={testResults.visual_inspection}
                      onChange={(v) => setTestResults({ ...testResults, visual_inspection: v })}
                      testId="recert-visual"
                    />
                  </div>
                </div>

                {/* 2. Gas Leak Test - Always Mandatory, File Upload for Trailer/Storage */}
                <div className="p-4 border rounded-lg">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <p className="font-medium">Gas Leak Test *</p>
                      <p className="text-sm text-muted-foreground">Check all connections and valves for leaks</p>
                    </div>
                    <TestResultButtons
                      value={testResults.gas_leak_test}
                      onChange={(v) => {
                        setTestResults({ ...testResults, gas_leak_test: v });
                        if (v === 'not_conducted' && isTrailerOrStorage) handleRemoveFile('leak_test_report');
                      }}
                      testId="recert-gas-leak"
                    />
                  </div>
                  {/* File Upload for Leak Test - Trailer/Storage, only when test conducted */}
                  {isTrailerOrStorage && testResults.gas_leak_test && testResults.gas_leak_test !== 'not_conducted' && (
                    <div className="mt-4 pt-4 border-t">
                      <Label className="text-sm font-medium flex items-center gap-2">
                        <Upload className="h-4 w-4" />
                        Upload Leak Test Report / Certificate *
                      </Label>
                      <p className="text-xs text-muted-foreground mb-2">PDF or Image (JPEG, PNG) - Required for {typeLabel} inspections</p>
                      {testReportFiles.leak_test_report ? (
                        <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-md">
                          <FileText className="h-4 w-4 text-primary" />
                          <span className="text-sm flex-1 truncate">{testReportFiles.leak_test_report.name}</span>
                          <Button variant="ghost" size="sm" onClick={() => handleRemoveFile('leak_test_report')}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      ) : (
                        <Input
                          type="file"
                          accept=".pdf,image/*"
                          onChange={(e) => handleFileUpload('leak_test_report', e.target.files?.[0])}
                          disabled={uploading.leak_test_report}
                          className="cursor-pointer"
                          data-testid="leak-test-upload"
                        />
                      )}
                    </div>
                  )}
                </div>

                {/* 3. NDT Test - Mandatory for Trailer/Storage */}
                {isTrailerOrStorage && (
                  <div className="p-4 border rounded-lg border-amber-200 bg-amber-50/30">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div>
                        <p className="font-medium">Non-Destructive Test (NDT) *</p>
                        <p className="text-sm text-muted-foreground">Ultrasonic/radiographic testing - mandatory for {typeLabel}</p>
                      </div>
                      <TestResultButtons
                        value={testResults.ndt_test}
                        onChange={(v) => {
                          setTestResults({ ...testResults, ndt_test: v });
                          if (v === 'not_conducted') handleRemoveFile('ndt_test_report');
                        }}
                        testId="recert-ndt-mandatory"
                      />
                    </div>
                    {/* File Upload for NDT Test, only when test conducted */}
                    {testResults.ndt_test && testResults.ndt_test !== 'not_conducted' && (
                    <div className="mt-4 pt-4 border-t border-amber-200">
                      <Label className="text-sm font-medium flex items-center gap-2">
                        <Upload className="h-4 w-4" />
                        Upload NDT Test Report / Certificate *
                      </Label>
                      <p className="text-xs text-muted-foreground mb-2">PDF or Image (JPEG, PNG) - Required for {typeLabel} inspections</p>
                      {testReportFiles.ndt_test_report ? (
                        <div className="flex items-center gap-2 p-2 bg-white rounded-md border">
                          <FileText className="h-4 w-4 text-primary" />
                          <span className="text-sm flex-1 truncate">{testReportFiles.ndt_test_report.name}</span>
                          <Button variant="ghost" size="sm" onClick={() => handleRemoveFile('ndt_test_report')}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      ) : (
                        <Input
                          type="file"
                          accept=".pdf,image/*"
                          onChange={(e) => handleFileUpload('ndt_test_report', e.target.files?.[0])}
                          disabled={uploading.ndt_test_report}
                          className="cursor-pointer"
                          data-testid="ndt-test-upload"
                        />
                      )}
                    </div>
                    )}
                  </div>
                )}
              </div>

              {/* Optional/Additional Tests Section */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  {isTrailerOrStorage ? 'Additional Tests' : 'Optional Tests'}
                </h3>
                
                {/* 4. Pressure Test - Optional, File Upload if Conducted for Trailer/Storage */}
                <div className="p-4 border rounded-lg bg-muted/20">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 w-full">
                    <div className="flex-1">
                      <p className="font-medium">Pressure Test</p>
                      <p className="text-sm text-muted-foreground">
                        Verify pressure holding capacity {isTrailerOrStorage ? '(file upload required if conducted)' : '(optional)'}
                      </p>
                    </div>
                    <TestResultButtons
                      value={testResults.pressure_test}
                      onChange={(v) => {
                        setTestResults({ ...testResults, pressure_test: v });
                        if (v === 'not_conducted') handleRemoveFile('pressure_test_report');
                      }}
                      testId="recert-pressure"
                    />
                  </div>
                  {/* File Upload for Pressure Test - Trailer/Storage, only when test conducted */}
                  {isTrailerOrStorage && testResults.pressure_test && testResults.pressure_test !== 'not_conducted' && (
                    <div className="mt-4 pt-4 border-t">
                      <Label className="text-sm font-medium flex items-center gap-2">
                        <Upload className="h-4 w-4" />
                        Upload Pressure Test Report / Certificate *
                      </Label>
                      <p className="text-xs text-muted-foreground mb-2">PDF or Image (JPEG, PNG) - Required when test is conducted</p>
                      {testReportFiles.pressure_test_report ? (
                        <div className="flex items-center gap-2 p-2 bg-white rounded-md border">
                          <FileText className="h-4 w-4 text-primary" />
                          <span className="text-sm flex-1 truncate">{testReportFiles.pressure_test_report.name}</span>
                          <Button variant="ghost" size="sm" onClick={() => handleRemoveFile('pressure_test_report')}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      ) : (
                        <Input
                          type="file"
                          accept=".pdf,image/*"
                          onChange={(e) => handleFileUpload('pressure_test_report', e.target.files?.[0])}
                          disabled={uploading.pressure_test_report}
                          className="cursor-pointer"
                          data-testid="pressure-test-upload"
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Overall Result Section - Mandatory */}
              <Separator className="my-6" />
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                  <Award className="h-4 w-4" />
                  Overall Inspection Result *
                </h3>
                <p className="text-sm text-muted-foreground">
                  Based on the test results above, select the final inspection outcome
                </p>
                
                <RadioGroup
                  value={testResults.overall_result || ''}
                  onValueChange={(v) => setTestResults({ ...testResults, overall_result: v })}
                  className="grid grid-cols-1 md:grid-cols-3 gap-4"
                >
                  {OVERALL_RESULTS.map((result) => (
                    <div key={result.value}>
                      <RadioGroupItem
                        value={result.value}
                        id={`overall-${result.value}`}
                        className="peer sr-only"
                      />
                      <Label
                        htmlFor={`overall-${result.value}`}
                        className={cn(
                          "flex flex-col items-center justify-center rounded-lg border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground cursor-pointer transition-all",
                          "peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5",
                          testResults.overall_result === result.value && result.value === 'approved' && "border-emerald-500 bg-emerald-50",
                          testResults.overall_result === result.value && result.value === 'conditionally_approved' && "border-amber-500 bg-amber-50",
                          testResults.overall_result === result.value && result.value === 'rejected' && "border-red-500 bg-red-50"
                        )}
                      >
                        <div className={cn(
                          "w-3 h-3 rounded-full mb-2",
                          result.color
                        )} />
                        <span className="font-semibold">{result.label}</span>
                        <span className="text-xs text-muted-foreground text-center mt-1">{result.description}</span>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>

                {/* Result Status Preview */}
                {testResults.overall_result && (
                  <div className={cn(
                    "p-4 rounded-lg border-2 mt-4",
                    testResults.overall_result === 'approved' && "bg-emerald-50 border-emerald-200",
                    testResults.overall_result === 'conditionally_approved' && "bg-amber-50 border-amber-200",
                    testResults.overall_result === 'rejected' && "bg-red-50 border-red-200"
                  )}>
                    <div className="flex items-center gap-2">
                      {testResults.overall_result === 'approved' && <CheckCircle className="h-5 w-5 text-emerald-600" />}
                      {testResults.overall_result === 'conditionally_approved' && <AlertTriangle className="h-5 w-5 text-amber-600" />}
                      {testResults.overall_result === 'rejected' && <XCircle className="h-5 w-5 text-red-600" />}
                      <span className={cn(
                        "font-semibold",
                        testResults.overall_result === 'approved' && "text-emerald-700",
                        testResults.overall_result === 'conditionally_approved' && "text-amber-700",
                        testResults.overall_result === 'rejected' && "text-red-700"
                      )}>
                        {testResults.overall_result === 'approved' && 'Inspection Approved - Certificate will be generated'}
                        {testResults.overall_result === 'conditionally_approved' && 'Conditionally Approved - Certificate will note conditions'}
                        {testResults.overall_result === 'rejected' && 'Inspection Rejected - No certificate will be generated'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <Card>
            <CardContent className="py-6">
              <div className="flex justify-between items-center">
                <Button variant="outline" onClick={() => setStep(1)}>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Search
                </Button>
                <Button 
                  onClick={handleSubmitClick} 
                  disabled={submitting}
                  size="lg"
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="recertify-submit"
                >
                  {submitting ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Shield className="mr-2 h-4 w-4" />
                  )}
                  Confirm Recertification
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Confirm Recertification
            </DialogTitle>
            <DialogDescription>
              Please review the details before proceeding
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-4 bg-muted rounded-lg space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Registration/Serial:</span>
                <span className="font-mono font-bold">
                  {selectedRecord?.registration_number || selectedRecord?.storage_serial_number || selectedRecord?.serial_number}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">New Inspection Date:</span>
                <span className="font-medium">{inspectionDate ? format(parseISO(inspectionDate), 'dd MMM yyyy') : '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Valid Until:</span>
                <span className="font-medium text-emerald-600">{validTo ? format(parseISO(validTo), 'dd MMM yyyy') : '-'}</span>
              </div>
              <Separator className="my-2" />
              <p className="text-sm font-semibold">Test Results:</p>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Visual Inspection:</span>
                <Badge variant={testResults.visual_inspection === 'pass' ? 'default' : 'destructive'}>
                  {testResults.visual_inspection?.toUpperCase() || 'N/A'}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Gas Leak Test:</span>
                <Badge variant={testResults.gas_leak_test === 'pass' ? 'default' : 'destructive'}>
                  {testResults.gas_leak_test?.toUpperCase() || 'N/A'}
                </Badge>
              </div>
              {testResults.ndt_test !== null && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">NDT Test:</span>
                  <Badge variant={testResults.ndt_test === 'pass' ? 'default' : 'destructive'}>
                    {testResults.ndt_test?.toUpperCase() || 'N/A'}
                  </Badge>
                </div>
              )}
              {testResults.pressure_test !== null && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Pressure Test:</span>
                  <Badge variant={testResults.pressure_test === 'pass' ? 'default' : 'destructive'}>
                    {testResults.pressure_test?.toUpperCase() || 'N/A'}
                  </Badge>
                </div>
              )}
              <Separator className="my-2" />
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground font-semibold">Overall Result:</span>
                <Badge 
                  variant="outline"
                  className={cn(
                    "text-sm font-bold",
                    testResults.overall_result === 'approved' && "bg-emerald-100 text-emerald-700 border-emerald-300",
                    testResults.overall_result === 'conditionally_approved' && "bg-amber-100 text-amber-700 border-amber-300",
                    testResults.overall_result === 'rejected' && "bg-red-100 text-red-700 border-red-300"
                  )}
                >
                  {testResults.overall_result?.replace('_', ' ').toUpperCase() || 'N/A'}
                </Badge>
              </div>
              {isTrailerOrStorage && (
                <>
                  <Separator className="my-2" />
                  <p className="text-sm font-semibold">Uploaded Reports:</p>
                  {testReportFiles.leak_test_report && (
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-emerald-600" />
                      <span>Leak Test Report</span>
                    </div>
                  )}
                  {testReportFiles.ndt_test_report && (
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-emerald-600" />
                      <span>NDT Test Report</span>
                    </div>
                  )}
                  {testReportFiles.pressure_test_report && (
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-emerald-600" />
                      <span>Pressure Test Report</span>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <p className="font-semibold mb-1">This action will:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Generate a new certificate number</li>
                <li>Update the QR verification code</li>
                <li>Update test results as shown above</li>
                <li>Record this renewal in the audit trail</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmSubmit}
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {submitting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle className="mr-2 h-4 w-4" />
              )}
              Confirm & Generate Certificate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
